// src/cockpit4/components/SalesFunnel.tsx
// ============================================
// P0-功能1：智能销售漏斗（客户中心）
//
// 功能：
//   1. 7 阶段销售漏斗：线索 → 接触 → 需求 → 方案 → 报价 → 谈判 → 成交
//   2. 每阶段显示：客户数 / 转化率 / 金额
//   3. 横向漏斗可视化（div + Tailwind，不引入新依赖）
//   4. AI 客户意向打分（0-100，基于 tier/金额/最近联系/状态综合计算）
//   5. 瓶颈阶段红色高亮（转化率低于阈值）
//   6. 客户卡片可点击查看详情
//
// 数据来源：
//   - RuntimeManager.query(RuntimeName.CRM, { type: "list" })
//   - 真实 CRM 客户列表，按 followup.status + tier + history 等字段推断所处阶段
//   - 无数据时显示空状态提示，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Filter,
  RefreshCw,
  Loader2,
  TrendingDown,
  AlertTriangle,
  Users,
  DollarSign,
  Trophy,
  X,
  Phone,
  Mail,
  MessageSquare,
  MapPin,
  Calendar,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { CustomerProfile, CustomerTier } from "../../runtime/CRMRuntime/types";
import { CUSTOMER_CATEGORY_CONFIG } from "../../runtime/CRMRuntime/types";

// ============================================
// 漏斗阶段定义
// ============================================

type FunnelStageId =
  | "lead" // 线索
  | "contact" // 接触
  | "requirement" // 需求
  | "proposal" // 方案
  | "quotation" // 报价
  | "negotiation" // 谈判
  | "closed"; // 成交

interface FunnelStageMeta {
  id: FunnelStageId;
  label: string;
  /** 主色（用于漏斗列条 + 文案） */
  color: string;
  /** 阶段序号（用于排序） */
  order: number;
}

const FUNNEL_STAGES: FunnelStageMeta[] = [
  { id: "lead", label: "线索", color: "#60A5FA", order: 1 },
  { id: "contact", label: "接触", color: "#38BDF8", order: 2 },
  { id: "requirement", label: "需求", color: "#A78BFA", order: 3 },
  { id: "proposal", label: "方案", color: "#F472B6", order: 4 },
  { id: "quotation", label: "报价", color: "#FBBF24", order: 5 },
  { id: "negotiation", label: "谈判", color: "#FB923C", order: 6 },
  { id: "closed", label: "成交", color: "#34D399", order: 7 },
];

// 等级权重（用于 AI 打分）
const TIER_SCORE_BONUS: Record<CustomerTier, number> = {
  "S+": 30,
  S: 25,
  A: 20,
  B: 10,
  C: 5,
};

const TIER_COLOR_MAP: Record<string, string> = {
  "S+": "#A855F7",
  S: "#EC4899",
  A: "#3B82F6",
  B: "#6B7280",
  C: "#4B5563",
};

// 瓶颈阈值：阶段转化率低于此值视为瓶颈
const BOTTLENECK_THRESHOLD = 30;

// ============================================
// 工具函数
// ============================================

const DAY_MS = 24 * 60 * 60 * 1000;

/** 计算距今天数（无效日期返回 -1） */
function daysSince(dateStr: string): number {
  if (!dateStr) return -1;
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return -1;
  return Math.floor((Date.now() - t) / DAY_MS);
}

/**
 * 根据客户档案推断所处漏斗阶段
 * 规则：
 *   - closed-won / active → 成交
 *   - negotiating → 谈判
 *   - prospecting 按 tier / 金额 / 历史记录细分：
 *     · tier ∈ {S+,S,A} 且 estimatedValue > 0 → 报价
 *     · tier ∈ {S+,S,A} → 方案
 *     · 有历史沟通内容 → 需求
 *     · 有 lastContact → 接触
 *     · 其余 → 线索
 *   - closed-lost 不进入漏斗（已流失）
 */
function inferStage(c: CustomerProfile): FunnelStageId | null {
  const status = c.followup?.status;
  if (!status) return null;
  if (status === "closed-lost") return null;
  if (status === "closed-won" || status === "active") return "closed";
  if (status === "negotiating") return "negotiation";

  // prospecting 细分
  const tier = c.tier;
  const value = c.followup?.estimatedValue || 0;
  const hasHistory = !!(c.history && c.history.trim().length > 0);
  const hasLastContact = !!(c.followup?.lastContact && !isNaN(new Date(c.followup.lastContact).getTime()));

  if ((tier === "S+" || tier === "S" || tier === "A") && value > 0) return "quotation";
  if (tier === "S+" || tier === "S" || tier === "A") return "proposal";
  if (hasHistory) return "requirement";
  if (hasLastContact) return "contact";
  return "lead";
}

/**
 * AI 客户意向打分（0-100）
 * 综合维度：
 *   - tier 等级（S+/S/A/B/C）
 *   - 预估金额（归一化到 0-20 分）
 *   - 最近联系（7 天内 +15，14 天内 +10，30 天内 +5，更久不加分，负向 -5）
 *   - 跟进状态（negotiating +15，closed-won/active +20，prospecting +0）
 *   - 是否有 history（+5）与 suggestion（+5）
 */
function scoreCustomer(c: CustomerProfile): number {
  let score = 50;
  score += TIER_SCORE_BONUS[c.tier] ?? 0;

  const value = c.followup?.estimatedValue || 0;
  // 金额归一化：10w 满分 20
  score += Math.min(20, Math.round((value / 100000) * 20));

  const d = daysSince(c.followup?.lastContact || "");
  if (d >= 0) {
    if (d <= 7) score += 15;
    else if (d <= 14) score += 10;
    else if (d <= 30) score += 5;
    else score -= 5;
  }

  const status = c.followup?.status;
  if (status === "negotiating") score += 15;
  if (status === "closed-won" || status === "active") score += 20;

  if (c.history && c.history.trim().length > 0) score += 5;
  if (c.suggestion && c.suggestion.trim().length > 0) score += 5;

  return Math.max(0, Math.min(100, score));
}

/** 意向分级 */
function intentLevel(score: number): { label: string; color: string } {
  if (score >= 80) return { label: "高意向", color: "#34D399" };
  if (score >= 60) return { label: "中意向", color: "#FBBF24" };
  if (score >= 40) return { label: "低意向", color: "#FB923C" };
  return { label: "待激活", color: "#6B7280" };
}

/** 格式化金额（万元） */
function formatAmount(amount: number): string {
  if (!amount || amount <= 0) return "¥0";
  if (amount >= 10000) return `¥${(amount / 10000).toFixed(1)}w`;
  return `¥${amount.toLocaleString()}`;
}

// ============================================
// 主组件
// ============================================

export default function SalesFunnel() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedStage, setSelectedStage] = useState<FunnelStageId | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 加载真实客户列表
  const loadCustomers = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      // 轮询等待 RuntimeManager 就绪
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setError("RuntimeManager 尚未初始化");
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "list" });
      if (resp?.success && resp.data) {
        const inner = resp.data as { data?: CustomerProfile[] } | CustomerProfile[];
        const list = Array.isArray(inner) ? inner : inner?.data || [];
        setCustomers(Array.isArray(list) ? list : []);
      } else {
        setError(resp?.error || "查询客户列表失败");
      }
    } catch (err) {
      setError((err as Error).message || "加载客户列表异常");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadCustomers();
  }, [loadCustomers]);

  // 按阶段分组 + AI 打分
  const stageData = useMemo(() => {
    const groups: Record<FunnelStageId, CustomerProfile[]> = {
      lead: [],
      contact: [],
      requirement: [],
      proposal: [],
      quotation: [],
      negotiation: [],
      closed: [],
    };
    const scored: { customer: CustomerProfile; score: number }[] = [];

    for (const c of customers) {
      const stage = inferStage(c);
      if (!stage) continue; // closed-lost 等不进入漏斗
      groups[stage].push(c);
      scored.push({ customer: c, score: scoreCustomer(c) });
    }

    // 每阶段统计
    const stats = FUNNEL_STAGES.map((meta, idx) => {
      const list = groups[meta.id];
      const count = list.length;
      const amount = list.reduce(
        (sum, c) => sum + (c.followup?.estimatedValue || 0),
        0
      );
      // 转化率：相对前一阶段客户数的百分比
      const prevCount = idx === 0 ? count : FUNNEL_STAGES.slice(0, idx).reduce((s, m) => s + groups[m.id].length, 0);
      const conversionRate = idx === 0
        ? 100
        : prevCount > 0
          ? Math.round((count / prevCount) * 100)
          : 0;
      const isBottleneck = idx > 0 && conversionRate < BOTTLENECK_THRESHOLD && count > 0;
      return { ...meta, count, amount, conversionRate, isBottleneck };
    });

    // 高意向客户 Top 5（按分数降序）
    const topIntent = scored
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    return { groups, stats, topIntent };
  }, [customers]);

  // 当前选中阶段的客户列表
  const selectedStageCustomers = useMemo(() => {
    if (!selectedStage) return [];
    return stageData.groups[selectedStage];
  }, [selectedStage, stageData]);

  // 整体统计
  const overview = useMemo(() => {
    const total = customers.length;
    const closedCount = stageData.groups.closed.length;
    const totalAmount = customers.reduce(
      (s, c) => s + (c.followup?.estimatedValue || 0),
      0
    );
    const overallConversion = total > 0 ? Math.round((closedCount / total) * 100) : 0;
    return { total, closedCount, totalAmount, overallConversion };
  }, [customers, stageData]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-blue-500/15 border border-blue-500/30">
            <Filter size={13} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">智能销售漏斗</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Sales Funnel · {overview.total} 客户 · 整体转化 {overview.overallConversion}%
            </p>
          </div>
        </div>
        <button
          onClick={loadCustomers}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-500/15 border border-blue-500/30 hover:bg-blue-500/25 transition-colors disabled:opacity-50"
        >
          {refreshing ? (
            <Loader2 size={11} className="text-blue-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-blue-400" />
          )}
          <span className="text-[9px] font-mono text-blue-300 uppercase tracking-wider">
            {refreshing ? "刷新中..." : "刷新"}
          </span>
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className="px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06] text-red-300 text-[10px] font-mono">
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-blue-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载漏斗数据...</p>
        </div>
      ) : customers.length === 0 ? (
        <div className="py-10 text-center">
          <Users size={24} className="mx-auto text-gray-600 mb-2" />
          <p className="text-[11px] font-mono text-gray-500">暂无客户数据</p>
          <p className="text-[9px] font-mono text-gray-700 mt-1">
            数据来源：RuntimeManager.query(CRM, {`{ type: "list" }`})
          </p>
        </div>
      ) : (
        <>
          {/* 顶部 KPI 概览 */}
          <div className="grid grid-cols-4 gap-2 p-4 border-b border-white/[0.04]">
            <OverviewKpi
              icon={Users}
              label="总客户数"
              value={String(overview.total)}
              color="#9CA3AF"
            />
            <OverviewKpi
              icon={Trophy}
              label="已成交"
              value={String(overview.closedCount)}
              color="#34D399"
            />
            <OverviewKpi
              icon={DollarSign}
              label="总金额"
              value={formatAmount(overview.totalAmount)}
              color="#FBBF24"
            />
            <OverviewKpi
              icon={TrendingDown}
              label="整体转化"
              value={`${overview.overallConversion}%`}
              color={overview.overallConversion >= 30 ? "#34D399" : "#F87171"}
            />
          </div>

          {/* 漏斗可视化（横向柱状漏斗） */}
          <div className="p-4 border-b border-white/[0.04]">
            <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-3">
              漏斗阶段分布（点击阶段查看客户）
            </div>
            <div className="space-y-2">
              {stageData.stats.map((s) => {
                const maxCount = Math.max(...stageData.stats.map((x) => x.count), 1);
                const widthPct = Math.max(8, Math.round((s.count / maxCount) * 100));
                const isBottleneck = s.isBottleneck;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSelectedStage(selectedStage === s.id ? null : s.id)}
                    className={`w-full flex items-center gap-3 p-2 rounded-md border transition-all text-left ${
                      selectedStage === s.id
                        ? "border-white/15 bg-white/[0.04]"
                        : "border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03]"
                    } ${isBottleneck ? "ring-1 ring-red-500/30" : ""}`}
                  >
                    {/* 阶段标签 */}
                    <div className="flex-shrink-0 w-16 flex items-center gap-1.5">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: s.color }}
                      />
                      <span className="text-[11px] font-mono text-gray-200">{s.label}</span>
                    </div>
                    {/* 漏斗柱 */}
                    <div className="flex-1 h-7 relative rounded bg-white/[0.02] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${widthPct}%` }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="h-full rounded relative"
                        style={{
                          background: isBottleneck
                            ? `linear-gradient(90deg, ${s.color}AA, #EF4444)`
                            : `linear-gradient(90deg, ${s.color}80, ${s.color})`,
                        }}
                      >
                        <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-mono font-bold text-white/90">
                          {s.count}
                        </span>
                      </motion.div>
                    </div>
                    {/* 转化率 + 金额 */}
                    <div className="flex-shrink-0 w-28 text-right">
                      <div
                        className={`text-[10px] font-mono font-bold ${
                          isBottleneck ? "text-red-400" : "text-gray-300"
                        }`}
                      >
                        {s.order === 1 ? "—" : `${s.conversionRate}%`}
                      </div>
                      <div className="text-[9px] font-mono text-emerald-400/80">
                        {formatAmount(s.amount)}
                      </div>
                    </div>
                    {isBottleneck && (
                      <AlertTriangle size={12} className="text-red-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* 图例 */}
            <div className="flex items-center justify-end gap-3 mt-3 pt-3 border-t border-white/[0.04]">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <span className="text-[9px] font-mono text-gray-500 uppercase">
                  瓶颈（转化 &lt; {BOTTLENECK_THRESHOLD}%）
                </span>
              </div>
            </div>
          </div>

          {/* 高意向客户 Top 5（AI 打分） */}
          <div className="p-4 border-b border-white/[0.04]">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={11} className="text-emerald-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                AI 高意向客户 Top 5
              </span>
            </div>
            {stageData.topIntent.length === 0 ? (
              <p className="text-[11px] font-mono text-gray-600 text-center py-3">暂无客户</p>
            ) : (
              <div className="space-y-1.5">
                {stageData.topIntent.map(({ customer, score }, idx) => {
                  const intent = intentLevel(score);
                  const catConfig = CUSTOMER_CATEGORY_CONFIG[customer.category] || CUSTOMER_CATEGORY_CONFIG.other;
                  return (
                    <div
                      key={customer.id}
                      onClick={() => setSelectedCustomer(customer)}
                      className="flex items-center gap-3 p-2 rounded-md border border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer transition-colors"
                    >
                      <span className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-[10px] font-mono font-bold text-gray-300 bg-white/[0.04]">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-gray-200 truncate">{customer.company}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span
                            className="w-1 h-1 rounded-full"
                            style={{ background: catConfig.color }}
                          />
                          <span className="text-[9px] font-mono text-gray-500">{catConfig.label}</span>
                          <span className="text-[9px] font-mono text-gray-600">·</span>
                          <span className="text-[9px] font-mono text-gray-500">{customer.owner}</span>
                        </div>
                      </div>
                      {/* 分数进度条 */}
                      <div className="flex-shrink-0 w-24">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[9px] font-mono" style={{ color: intent.color }}>
                            {intent.label}
                          </span>
                          <span className="text-[10px] font-mono font-bold" style={{ color: intent.color }}>
                            {score}
                          </span>
                        </div>
                        <div className="h-1 rounded-full bg-white/[0.05] overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${score}%`, background: intent.color }}
                          />
                        </div>
                      </div>
                      <ChevronRight size={11} className="text-gray-600 flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 选中阶段的客户列表 */}
          <AnimatePresence>
            {selectedStage && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden border-b border-white/[0.04]"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                      {FUNNEL_STAGES.find((s) => s.id === selectedStage)?.label} 阶段客户（{selectedStageCustomers.length}）
                    </span>
                    <button
                      onClick={() => setSelectedStage(null)}
                      className="text-[9px] font-mono text-gray-600 hover:text-gray-400"
                    >
                      收起
                    </button>
                  </div>
                  {selectedStageCustomers.length === 0 ? (
                    <p className="text-[11px] font-mono text-gray-600 text-center py-3">该阶段暂无客户</p>
                  ) : (
                    <div className="space-y-1 max-h-[300px] overflow-y-auto pr-1">
                      {selectedStageCustomers.map((c) => {
                        const score = scoreCustomer(c);
                        const intent = intentLevel(score);
                        const catConfig = CUSTOMER_CATEGORY_CONFIG[c.category] || CUSTOMER_CATEGORY_CONFIG.other;
                        return (
                          <div
                            key={c.id}
                            onClick={() => setSelectedCustomer(c)}
                            className="flex items-center gap-2 p-2 rounded-md border border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] text-gray-200 truncate">{c.company}</div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span
                                  className="px-1 rounded text-[8px] font-mono font-bold"
                                  style={{
                                    color: TIER_COLOR_MAP[c.tier] || "#6B7280",
                                    background: `${TIER_COLOR_MAP[c.tier] || "#6B7280"}20`,
                                  }}
                                >
                                  {c.tier}
                                </span>
                                <span
                                  className="w-1 h-1 rounded-full"
                                  style={{ background: catConfig.color }}
                                />
                                <span className="text-[9px] font-mono text-gray-500">{catConfig.label}</span>
                                <span className="text-[9px] font-mono text-gray-600">·</span>
                                <span className="text-[9px] font-mono text-gray-500">{c.owner}</span>
                              </div>
                            </div>
                            <span className="text-[10px] font-mono text-emerald-400 font-semibold flex-shrink-0">
                              {formatAmount(c.followup?.estimatedValue || 0)}
                            </span>
                            <span
                              className="text-[9px] font-mono px-1.5 py-0.5 rounded flex-shrink-0"
                              style={{ background: `${intent.color}20`, color: intent.color }}
                            >
                              {score}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {/* 客户详情 Modal */}
      <AnimatePresence>
        {selectedCustomer && (
          <CustomerDetailModal
            customer={selectedCustomer}
            score={scoreCustomer(selectedCustomer)}
            onClose={() => setSelectedCustomer(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 概览 KPI 小卡片
// ============================================
interface OverviewKpiProps {
  icon: typeof Users;
  label: string;
  value: string;
  color: string;
}

function OverviewKpi({ icon: Icon, label, value, color }: OverviewKpiProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">
          {label}
        </div>
        <div className="text-[13px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 客户详情 Modal（含 AI 打分）
// ============================================
interface CustomerDetailModalProps {
  customer: CustomerProfile;
  score: number;
  onClose: () => void;
}

function CustomerDetailModal({ customer, score, onClose }: CustomerDetailModalProps) {
  const catConfig = CUSTOMER_CATEGORY_CONFIG[customer.category] || CUSTOMER_CATEGORY_CONFIG.other;
  const intent = intentLevel(score);
  const stage = inferStage(customer);
  const stageMeta = FUNNEL_STAGES.find((s) => s.id === stage);

  const sections = [
    { key: "history", label: "历史沟通", icon: MessageSquare, color: "#3B82F6", content: customer.history },
    { key: "deals", label: "成交记录", icon: DollarSign, color: "#10B981", content: customer.deals },
    { key: "analysis", label: "AI 分析", icon: Sparkles, color: "#A855F7", content: customer.analysis },
    { key: "suggestion", label: "下一步建议", icon: TrendingDown, color: "#F59E0B", content: customer.suggestion },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-[760px] max-h-[85vh] rounded-xl border border-white/10 bg-[#0a0e1a]/95 backdrop-blur-xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${catConfig.color}20`, border: `1px solid ${catConfig.color}40` }}
            >
              <Users size={18} style={{ color: catConfig.color }} />
            </div>
            <div>
              <h3 className="text-[16px] font-semibold text-white">{customer.company}</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-mono"
                  style={{ background: `${catConfig.color}20`, color: catConfig.color }}
                >
                  {catConfig.label}
                </span>
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold"
                  style={{
                    color: TIER_COLOR_MAP[customer.tier] || "#6B7280",
                    background: `${TIER_COLOR_MAP[customer.tier] || "#6B7280"}20`,
                  }}
                >
                  {customer.tier}
                </span>
                {stageMeta && (
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] font-mono"
                    style={{ background: `${stageMeta.color}20`, color: stageMeta.color }}
                  >
                    {stageMeta.label}
                  </span>
                )}
                <span className="text-[10px] font-mono text-gray-500">{customer.industry}</span>
                <span className="flex items-center gap-0.5 text-[10px] font-mono text-gray-500">
                  <MapPin size={9} /> {customer.region}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* AI 意向打分条 */}
        <div className="px-5 py-3 border-b border-white/[0.06] bg-white/[0.01]">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <Sparkles size={11} className="text-emerald-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                AI 意向打分
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold"
                style={{ background: `${intent.color}20`, color: intent.color }}
              >
                {intent.label}
              </span>
              <span className="text-[14px] font-mono font-bold" style={{ color: intent.color }}>
                {score}
              </span>
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${score}%` }}
              transition={{ duration: 0.6 }}
              className="h-full rounded-full"
              style={{ background: intent.color }}
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* 联系人 + 跟进信息 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="text-[10px] font-mono text-gray-500 uppercase mb-2">联系人信息</div>
              <div className="space-y-1.5 text-[11px] font-mono">
                <div className="flex items-center gap-2 text-gray-300">
                  <Users size={11} className="text-gray-500" />
                  {customer.contact.name || "—"}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Phone size={11} className="text-gray-500" />
                  {customer.contact.phone || "—"}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <MessageSquare size={11} className="text-gray-500" />
                  {customer.contact.wechat || "—"}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Mail size={11} className="text-gray-500" />
                  {customer.contact.email || "—"}
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="text-[10px] font-mono text-gray-500 uppercase mb-2">跟进信息</div>
              <div className="space-y-1.5 text-[11px] font-mono">
                <div className="flex items-center gap-2 text-gray-400">
                  <Calendar size={11} className="text-gray-500" />
                  上次联系：{customer.followup.lastContact || "—"}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Calendar size={11} className="text-gray-500" />
                  下次联系：{customer.followup.nextContact || "—"}
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <DollarSign size={11} className="text-gray-500" />
                  预估金额：
                  <span className="text-emerald-400 font-semibold">
                    {customer.followup.estimatedValue > 0
                      ? `¥${customer.followup.estimatedValue.toLocaleString()}`
                      : "—"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <Users size={11} className="text-gray-500" />
                  负责人：{customer.owner}
                </div>
              </div>
            </div>
          </div>

          {/* 标签 */}
          {customer.tags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {customer.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-blue-500/10 text-blue-400 border border-blue-500/20"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* 档案章节 */}
          {sections.map((s) => (
            <div key={s.key} className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-3">
              <div className="flex items-center gap-2 mb-2">
                <s.icon size={12} style={{ color: s.color }} />
                <span className="text-[11px] font-mono uppercase tracking-wider" style={{ color: s.color }}>
                  {s.label}
                </span>
              </div>
              <div className="text-[11px] text-gray-300 leading-relaxed whitespace-pre-wrap">
                {s.content || "暂无记录"}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
          <span className="text-[10px] font-mono text-gray-600">
            档案路径：{customer.filePath}
          </span>
          <button
            onClick={onClose}
            className="px-3 py-1 rounded-md text-[11px] font-mono text-gray-300 bg-white/[0.05] hover:bg-white/[0.1] transition-colors"
          >
            关闭
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
